import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/globals.css';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Journey from './pages/Journey';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Hours from './pages/Hours';
import Learning from './pages/Learning';
import LearningItemView from './components/learning/LearningItemView';
import BusinessDetail from './pages/BusinessDetail';
import BusinessAssessmentForm from './pages/BusinessAssessmentForm';
import ProcessMaps from './pages/ProcessMaps';
import ProcessMapsList from './pages/ProcessMapsList';
import ProcessMapBuilder from './pages/ProcessMapBuilder';
import Solutions from './pages/Solutions';
import SolutionMatches from './pages/SolutionMatches';
import ProjectDetail from './pages/ProjectDetail';
import MentorDashboard from './pages/mentor/MentorDashboard';
import InternDetail from './pages/mentor/InternDetail';
import Pilots from './pages/Pilots';
import PilotDetail from './pages/PilotDetail';
import Evidence from './pages/Evidence';
import Reflections from './pages/Reflections';
import ProfessionalJourney from './pages/ProfessionalJourney';
import Completion from './pages/Completion';
import VerifyCertificate from './pages/VerifyCertificate';
import Reports from './pages/Reports';
import AICoach from './pages/AICoach';
import Layout from './components/layout/Layout';
import Tasks from './pages/Tasks';
import ComingSoon from './pages/ComingSoon';
import MyMentor from './pages/MyMentor';
import HelpSupport from './pages/HelpSupport';
import MentorInterns from './pages/mentor/MentorInterns';
import MentorLearningModules from './pages/mentor/MentorLearningModules';
import InternLearningModules from './pages/InternLearningModules';
import InternModuleView from './pages/InternModuleView';
import Notifications from './pages/Notifications';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminInterns from './pages/admin/AdminInterns';
import AdminUserInfo from './pages/admin/AdminUserInfo';
import AdminCohorts from './pages/admin/AdminCohorts';
import AdminStaff from './pages/admin/AdminStaff';
import AdminHours from './pages/admin/AdminHours';
import AdminSupport from './pages/admin/AdminSupport';
import AdminSettings from './pages/admin/AdminSettings';
import AdminOnboarding from './pages/admin/AdminOnboarding';
import Onboarding from './pages/Onboarding';
import ProjectsHub from './pages/ProjectsHub';
import PracticalChallenges from './features/challenges/pages/PracticalChallenges';
import ChallengeWorkspace from './features/challenges/pages/ChallengeWorkspace';
import ChallengeReviews from './features/challenges/pages/ChallengeReviews';
import ChallengeReviewDetail from './features/challenges/pages/ChallengeReviewDetail';
import ChallengeReviewPrint from './features/challenges/pages/ChallengeReviewPrint';
import ManageChallenges from './features/challenges/pages/ManageChallenges';
import ChallengeBuilder from './features/challenges/pages/ChallengeBuilder';
import AdminCareerDirections from './features/careerDirection/AdminCareerDirections';
import { getHomePath } from './config/navigation';

const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, navRole, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(navRole)) {
        return <Navigate to={getHomePath(navRole)} replace />;
    }

    return children;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            retry: 1,
        },
    },
});

function AppRoutes() {
    const { isAuthenticated, loading, navRole } = useAuth();
    const homePath = getHomePath(navRole);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            </div>
        );
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={homePath} replace />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Talent profile onboarding — full-screen journey (resume links work without login) */}
            <Route path="/onboarding" element={<Onboarding />} />
            
            {/* Protected routes */}
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout>
                        <Navigate to={homePath} replace />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Layout>
                        <Dashboard />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
                <ProtectedRoute>
                    <Layout>
                        <Profile />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/profile-builder" element={<Navigate to="/profile" replace />} />
            <Route path="/portfolio" element={<Navigate to="/professional-journey" replace />} />
            
            <Route path="/journey" element={
                <ProtectedRoute>
                    <Layout>
                        <Journey />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/hours" element={
                <ProtectedRoute>
                    <Layout>
                        <Hours />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/learning" element={
                <ProtectedRoute>
                    <Layout>
                        <Learning />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/learning/item/:itemId" element={
                <ProtectedRoute>
                    <Layout>
                        <LearningItemView />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/learning-modules" element={
                <ProtectedRoute>
                    <Layout>
                        <InternLearningModules />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/learning-modules/:id" element={
                <ProtectedRoute>
                    <Layout>
                        <InternModuleView />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/business" element={
                <ProtectedRoute>
                    <Layout>
                        <ProjectsHub tab="client" />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/business/:businessId" element={
                <ProtectedRoute>
                    <Layout>
                        <BusinessDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/business/:businessId/assessment" element={
                <ProtectedRoute>
                    <Layout>
                        <BusinessAssessmentForm />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/business/:businessId/process-maps" element={
                <ProtectedRoute>
                    <Layout>
                        <ProcessMapsList />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/business/:businessId/process-map/builder" element={
                <ProtectedRoute>
                    <Layout>
                        <ProcessMapBuilder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/process-mapping" element={
                <ProtectedRoute>
                    <Layout>
                        <ProcessMapBuilder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/process-mapping/:mapId" element={
                <ProtectedRoute>
                    <Layout>
                        <ProcessMapBuilder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/solutions" element={
                <ProtectedRoute>
                    <Layout>
                        <Solutions />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/solutions/matches" element={
                <ProtectedRoute>
                    <Layout>
                        <SolutionMatches />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/solutions/:solutionId" element={
                <ProtectedRoute>
                    <Layout>
                        <Solutions />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/projects" element={
                <ProtectedRoute>
                    <Layout>
                        <ProjectsHub tab="internship" />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/projects/:projectId" element={
                <ProtectedRoute>
                    <Layout>
                        <ProjectDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/dashboard" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <MentorDashboard />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/interns" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <MentorInterns />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/interns/:internId" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <InternDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/learning-modules" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <MentorLearningModules />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/practical-challenges" element={
                <ProtectedRoute>
                    <Layout>
                        <PracticalChallenges />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/practical-challenges/:challengeId" element={
                <ProtectedRoute>
                    <Layout>
                        <ChallengeWorkspace />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/challenge-reviews" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <ChallengeReviews />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/challenge-reviews/:submissionId" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <ChallengeReviewDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            {/* Printable copy for "Download PDF" — no app chrome */}
            <Route path="/mentor/challenge-reviews/:submissionId/print" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <ChallengeReviewPrint />
                </ProtectedRoute>
            } />

            <Route path="/mentor/practical-challenges" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <ManageChallenges />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/mentor/practical-challenges/:id/edit" element={
                <ProtectedRoute allowedRoles={['mentor', 'admin']}>
                    <Layout>
                        <ChallengeBuilder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/career-directions" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminCareerDirections />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/practical-challenges" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <ManageChallenges />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/practical-challenges/:id/edit" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <ChallengeBuilder />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/pilots" element={
                <ProtectedRoute>
                    <Layout>
                        <Pilots />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/pilots/:pilotId" element={
                <ProtectedRoute>
                    <Layout>
                        <PilotDetail />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/evidence" element={
                <ProtectedRoute>
                    <Layout>
                        <Evidence />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/reflections" element={
                <ProtectedRoute>
                    <Layout>
                        <Reflections />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/professional-journey" element={
                <ProtectedRoute>
                    <Layout>
                        <ProfessionalJourney />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/completion" element={
                <ProtectedRoute>
                    <Layout>
                        <Completion />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <Reports />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/dashboard" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminDashboard />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/interns" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminInterns />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/user-info" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminUserInfo />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/onboarding" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminOnboarding />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/cohorts" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminCohorts />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/staff" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminStaff />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/hours" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminHours />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/support" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminSupport />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/reports" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <Reports />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/admin/settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                        <AdminSettings />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/notifications" element={
                <ProtectedRoute>
                    <Layout>
                        <Notifications />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/tasks" element={
                <ProtectedRoute>
                    <Layout>
                        <Tasks />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/skills" element={
                <ProtectedRoute>
                    <Layout>
                        <ComingSoon title="My Skills" />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/my-mentor" element={
                <ProtectedRoute>
                    <Layout>
                        <MyMentor />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/help" element={
                <ProtectedRoute>
                    <Layout>
                        <HelpSupport />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/ai-coach" element={
                <ProtectedRoute>
                    <Layout>
                        <AICoach />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/verify/:token" element={<VerifyCertificate />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to={isAuthenticated ? homePath : '/login'} replace />} />
        </Routes>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Router>
                <AuthProvider>
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#FFFFFF',
                                color: '#232727',
                                border: '1px solid #e4e8e7',
                                borderRadius: '0.75rem',
                                boxShadow: '0 4px 12px rgba(15, 34, 33, 0.08)',
                                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                            },
                            success: {
                                iconTheme: {
                                    primary: '#275954',
                                    secondary: '#FFFFFF',
                                },
                            },
                            error: {
                                iconTheme: {
                                    primary: '#b91c1c',
                                    secondary: '#FFFFFF',
                                },
                            },
                        }}
                    />
                    
                    <AppRoutes />
                </AuthProvider>
            </Router>
        </QueryClientProvider>
    );
}

export default App;
